/** Admin: Trader Crypto Pro settings (swap fee, transfer fee, minimum), Pro accounts and transfers. */
import { useCallback, useEffect, useState } from 'react';
import { D, fmtDateTime, fmtUsd, MAX_SWAP_FEE_BPS, parseCryptoProSettings, shortAddress, type CryptoProSettings, type ProTransfer } from '@platform/shared';
import { ClientCell } from '../components/ClientCell';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'num h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

interface ProAdminAccount {
  userId: string;
  email: string;
  name: string | null;
  externalClientId: string | null;
  address: string;
  provider: string;
  status: 'active' | 'frozen';
  movedIn: string;
  movedOut: string;
  pending: number;
  createdAt: string;
}

interface AdminData {
  settings: CryptoProSettings;
  chain: { name: string; simulated: boolean } | null;
  updatedBy: string | null;
  updatedAt: string | null;
  accounts: ProAdminAccount[];
  transfers: (ProTransfer & { email: string; name: string | null; externalClientId: string | null })[];
}

/** Settings form keeps every value as typed text. */
type Form = { enabled: boolean; swapFeePct: string; transferFeeUsd: string; minTransferUsd: string; solSeed: string };
const toForm = (s: CryptoProSettings): Form => ({
  enabled: s.enabled,
  swapFeePct: D.from(s.swapFeeBps).div(100).toString(),
  transferFeeUsd: s.transferFeeUsd,
  minTransferUsd: s.minTransferUsd,
  solSeed: s.solSeed,
});

function Num({ label, value, onChange, hint, prefix, suffix }: { label: string; value: string; onChange: (v: string) => void; hint?: string; prefix?: string; suffix?: string }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-[11px] text-muted">
      {label}
      <span className="relative">
        {prefix && <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs text-muted">{prefix}</span>}
        <input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))} className={cx(input, prefix && 'pl-6', suffix && 'pr-10')} />
        {suffix && <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted">{suffix}</span>}
      </span>
      {hint && <span>{hint}</span>}
    </label>
  );
}

export function CryptoProTab() {
  const [data, setData] = useState<AdminData | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [f, setF] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = useCallback(async () => {
    try {
      const d = await api.get<AdminData>('/api/admin/crypto-pro');
      setData(d);
      setF((cur) => cur ?? toForm(d.settings));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(err(e));
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);

  if (loadErr) return <p className="p-4 text-down">{loadErr}</p>;
  if (!data || !f) return <div className="grid place-items-center p-10"><Spinner /></div>;

  const pct = f.swapFeePct === '' || f.swapFeePct === '.' ? null : D.from(f.swapFeePct);
  const bps = pct ? Number(pct.mul(100).round(0).toString()) : NaN;
  const settings = { enabled: f.enabled, swapFeeBps: bps, transferFeeUsd: f.transferFeeUsd, minTransferUsd: f.minTransferUsd, solSeed: f.solSeed };
  const parsed = parseCryptoProSettings(settings);
  const fractional = pct && !pct.mul(100).round(0).eq(pct.mul(100));

  const save = async () => {
    if (!parsed.ok) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.put<{ settings: CryptoProSettings }>('/api/admin/crypto-pro/settings', { settings: parsed.value });
      setF(toForm(r.settings));
      setMsg({ ok: true, text: 'Saved. New swaps and transfers use these values.' });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (a: ProAdminAccount) => {
    try {
      await api.post(`/api/admin/crypto-pro/accounts/${a.userId}/status`, { status: a.status === 'active' ? 'frozen' : 'active' });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    }
  };

  return (
    <div className="flex max-w-[1100px] flex-col gap-4 p-4">
      <section className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="m-0 text-sm font-semibold">Crypto Pro settings</h2>
            <p className="m-0 mt-0.5 text-xs text-muted">
              Fees for the Crypto Pro account. Changes apply to new swaps and transfers.
              {data.updatedAt && ` Last changed ${fmtDateTime(data.updatedAt)}${data.updatedBy ? ` by ${data.updatedBy}` : ''}.`}
            </p>
          </div>
          <span className={cx('rounded-md px-2 py-1 text-[11px] font-semibold', !data.chain ? 'bg-down-bg text-down' : data.chain.simulated ? 'bg-warn-bg text-warn' : 'bg-up-bg text-up')}>
            {!data.chain ? 'No wallet provider configured: hidden from clients' : data.chain.simulated ? 'Simulated chain (test mode)' : `Chain: ${data.chain.name}`}
          </span>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[13px]">
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} className="m-0 size-[15px]" />
          Offer Crypto Pro to clients
        </label>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Num label="Swap fee" suffix="%" value={f.swapFeePct} onChange={(v) => setF({ ...f, swapFeePct: v })} hint={`Per buy or sell. 0–${MAX_SWAP_FEE_BPS / 100}%, in steps of 0.01%.`} />
          <Num label="Transfer fee" prefix="$" value={f.transferFeeUsd} onChange={(v) => setF({ ...f, transferFeeUsd: v })} hint="Per move between Live and Crypto Pro." />
          <Num label="Minimum transfer" prefix="$" value={f.minTransferUsd} onChange={(v) => setF({ ...f, minTransferUsd: v })} />
          <Num label="SOL for network fees" suffix="SOL" value={f.solSeed} onChange={(v) => setF({ ...f, solSeed: v })} hint="Sent with a new wallet's first transfer." />
        </div>
        {(fractional || !parsed.ok) && <p className="m-0 text-xs text-warn">{fractional ? 'Swap fee can have at most two decimals (0.01%).' : !parsed.ok ? parsed.error : ''}</p>}
        {msg && <p className={cx('m-0 text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
        <div className="flex gap-2">
          <button type="button" className={primary} disabled={busy || !parsed.ok || !!fractional} onClick={() => void save()}>
            {busy ? <Spinner /> : 'Save settings'}
          </button>
          <button type="button" className={btn} onClick={() => setF(toForm(data.settings))}>
            Reset
          </button>
        </div>
      </section>

      <section className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-4">
        <h2 className="m-0 text-sm font-semibold">Crypto Pro accounts ({data.accounts.length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-[12.5px]">
            <thead className="text-[11px] text-muted">
              <tr>
                <th className="py-1.5 font-medium">Client</th>
                <th className="font-medium">Wallet</th>
                <th className="font-medium">Moved in</th>
                <th className="font-medium">Moved out</th>
                <th className="font-medium">Pending</th>
                <th className="font-medium">Opened</th>
                <th className="font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.userId} className="border-t border-line">
                  <td className="py-2">
                    <ClientCell name={a.name} email={a.email} crmId={a.externalClientId} />
                  </td>
                  <td className="num">{shortAddress(a.address, 6, 6)}</td>
                  <td className="num">{fmtUsd(a.movedIn)}</td>
                  <td className="num">{fmtUsd(a.movedOut)}</td>
                  <td className={cx('num', a.pending > 0 && 'text-warn')}>{a.pending}</td>
                  <td>{fmtDateTime(a.createdAt)}</td>
                  <td className={a.status === 'frozen' ? 'text-down' : 'text-up'}>{a.status}</td>
                  <td className="text-right">
                    <button type="button" className={btn} onClick={() => void setStatus(a)}>
                      {a.status === 'active' ? 'Freeze' : 'Unfreeze'}
                    </button>
                  </td>
                </tr>
              ))}
              {!data.accounts.length && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-muted">
                    No client has opened Crypto Pro yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-4">
        <h2 className="m-0 text-sm font-semibold">Recent transfers</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-[12.5px]">
            <thead className="text-[11px] text-muted">
              <tr>
                <th className="py-1.5 font-medium">Time</th>
                <th className="font-medium">Client</th>
                <th className="font-medium">Direction</th>
                <th className="font-medium">Amount</th>
                <th className="font-medium">Fee</th>
                <th className="font-medium">USDC</th>
                <th className="font-medium">Status</th>
                <th className="font-medium">Transaction</th>
              </tr>
            </thead>
            <tbody>
              {data.transfers.map((t) => (
                <tr key={t.id} className="border-t border-line">
                  <td className="py-2">{fmtDateTime(t.createdAt)}</td>
                  <td>
                    <ClientCell name={t.name} email={t.email} crmId={t.externalClientId} />
                  </td>
                  <td>{t.direction === 'to_pro' ? 'Live → Pro' : 'Pro → Live'}</td>
                  <td className="num">{fmtUsd(t.amount)}</td>
                  <td className="num">{fmtUsd(t.fee)}</td>
                  <td className="num">{t.usdc}</td>
                  <td className={t.status === 'confirmed' ? 'text-up' : t.status === 'failed' ? 'text-down' : 'text-warn'} title={t.error ?? undefined}>
                    {t.status}
                  </td>
                  <td className="num">{t.txSig ? shortAddress(t.txSig, 6, 6) : '—'}</td>
                </tr>
              ))}
              {!data.transfers.length && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-muted">
                    No transfers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
